import { User } from 'src/shared/types/User';
import { KeyManager } from 'src/utils/services/KeyManager';
import { publishIntercomAvailability } from 'src/shared/federation/utils/publishIntercomAvailability';
import { publishIntercomMessengerAvailability } from 'src/shared/federation/utils/publishIntercomMessengerAvailability';

interface Config {
  INTERCOM_APP_URL: string;
}

let intercomSettings: IntercomSettings | null = null;

const initialize = (config: Config, user: User, additionalData: Record<string, any> = {}): void => {
  if (!config.INTERCOM_APP_URL) {
    console.warn('Intercom is not configured properly.');
    publishIntercomAvailability(false);
    publishIntercomMessengerAvailability(false);
    return;
  }

  if (!user || !user.userId) {
    console.warn('User data is required to initialize Intercom.');
    publishIntercomAvailability(false);
    publishIntercomMessengerAvailability(false);
    return;
  }

  KeyManager.getTokenFor(user.beneId)
    .then((token) => {
      window.Intercom?.('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: token.appId,
        intercom_user_jwt: token.jwt,
      });

      window.intercomSettings = {
        app_id: token.appId,
        intercom_user_jwt: token.jwt,
        name: user.fullName,
        email: user.email,
        phone: user.phoneNumber,
        user_id: user.userId,
        userType: user.userType?.role ?? undefined,
        ...(user.companyId
          ? {
              company: {
                company_id: user.companyId,
                name: user.companyName,
              },
            }
          : {}),
        ...additionalData,
      };
      intercomSettings = window.intercomSettings || null;
      KeyManager.getKeyFor(config.INTERCOM_APP_URL)
        .then((appUrlKey) => {
          if (typeof window.Intercom !== 'function') {
            injectIntercomScript(appUrlKey);
          }

          update(window.intercomSettings!);
          publishIntercomAvailability(token.companyHasIntercom === true);
          publishIntercomMessengerAvailability(token.companyHasIntercomMessenger === true);
        })
        .catch((err) => {
          console.error('Failed to resolve Intercom script URL:', err);
          publishIntercomAvailability(false);
          publishIntercomMessengerAvailability(false);
        });
    })
    .catch((error) => {
      console.error('Failed to initialize Intercom:', error);
      publishIntercomAvailability(false);
      publishIntercomMessengerAvailability(false);
    });
};

const update = (additionalData: Record<string, any> = {}): void => {
  if (typeof window.Intercom === 'function' && intercomSettings) {
    intercomSettings = { ...intercomSettings, ...additionalData };
    window.Intercom('update', intercomSettings);
  }
};

const shutdown = (): void => {
  publishIntercomAvailability(false);
  publishIntercomMessengerAvailability(false);
  if (typeof window.Intercom === 'function') {
    window.Intercom('shutdown');
  }
};

const clearSession = (): void => {
  intercomSettings = null;
  publishIntercomAvailability(false);
  publishIntercomMessengerAvailability(false);
  if (typeof window.Intercom === 'function') {
    window.Intercom('shutdown');
  }
};

const show = (): void => {
  if (typeof window.Intercom === 'function') window.Intercom('show');
};

const showWithMessage = (message?: string): void => {
  if (typeof window.Intercom !== 'function') return;
  if (message?.trim()) window.Intercom('showNewMessage', message);
  else window.Intercom('show');
};

const injectIntercomScript = (intercomUrl: string): void => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = intercomUrl;

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);

  script.onload = () => {
    if (typeof window.Intercom === 'function') {
      window.Intercom('reattach_activator');
    }
  };
};

export const IntercomService = {
  initialize,
  update,
  shutdown,
  clearSession,
  show,
  showWithMessage,
};
